import { getAccessToken } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import { EmploymentInsuranceApiClient } from './employment-insurance-api-client';
import type { GuidedConversationView, GuidedEvent } from './guided-reporting';
import { EiApplicationInput, EiApplicationPreFill, EiClaim, EiReport, EiReportingStatus } from './models';

/** Local dev / integration tests: calls the real employment-insurance-bff over HTTP. */
export class HttpEmploymentInsuranceApiClient implements EmploymentInsuranceApiClient {
  constructor(private readonly baseUrl: string) {}

  /**
   * Attaches the mock-idp-issued bearer token (if signed in) so
   * employment-insurance-bff can independently verify it -- see mfe-pot's
   * plan doc. Nothing here decodes the token itself; it's forwarded as an
   * opaque credential.
   */
  private authHeaders(extra?: Record<string, string>): HeadersInit {
    const token = getAccessToken();
    return { ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }

  async applyForEi(applicantSub: string, application: EiApplicationInput): Promise<EiClaim> {
    const response = await fetch(`${this.baseUrl}/api/v1/applications`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ applicantSub, application }),
    });
    if (!response.ok) {
      throw new Error(`employment-insurance-bff returned ${response.status} for POST /api/v1/applications`);
    }
    return (await response.json()) as EiClaim;
  }

  /**
   * The BFF always answers 200 here -- a null pair means epayroll-ube was
   * unreachable, which the confirm step renders by omitting those rows. A
   * genuine transport failure (BFF itself down) is caught here and
   * flattened to the same null pair: a missing reference row must never
   * block the application.
   */
  async getApplicationPreFill(): Promise<EiApplicationPreFill> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/application/pre-fill`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) {
        return { insurableHours: null, insurableEarnings: null };
      }
      return (await response.json()) as EiApplicationPreFill;
    } catch {
      return { insurableHours: null, insurableEarnings: null };
    }
  }

  async getClaim(applicantSub: string): Promise<EiClaim | null> {
    const url = new URL(`${this.baseUrl}/api/v1/claims`);
    url.searchParams.set('applicantSub', applicantSub);
    const response = await fetch(url, { headers: this.authHeaders() });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`employment-insurance-bff returned ${response.status} for /api/v1/claims`);
    }
    return (await response.json()) as EiClaim;
  }

  async getReportingStatus(applicantSub: string): Promise<EiReportingStatus | null> {
    const url = new URL(`${this.baseUrl}/api/v1/reporting-status`);
    url.searchParams.set('applicantSub', applicantSub);
    const response = await fetch(url, { headers: this.authHeaders() });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`employment-insurance-bff returned ${response.status} for /api/v1/reporting-status`);
    }
    return (await response.json()) as EiReportingStatus;
  }

  async submitReport(
    claimId: string,
    applicantSub: string,
    periodStart: string,
    periodEnd: string,
    workedHours: number,
    earnings: number,
  ): Promise<EiReport> {
    const response = await fetch(`${this.baseUrl}/api/v1/reports`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ claimId, applicantSub, periodStart, periodEnd, workedHours, earnings }),
    });
    if (!response.ok) {
      throw new Error(`employment-insurance-bff returned ${response.status} for POST /api/v1/reports`);
    }
    return (await response.json()) as EiReport;
  }

  async endClaim(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/v1/claims`, {
      method: 'DELETE',
      headers: this.authHeaders(),
    });
    if (!response.ok) {
      throw new Error(`employment-insurance-bff returned ${response.status} for DELETE /api/v1/claims`);
    }
  }

  async getGuidedConversation(periodStart: string): Promise<GuidedConversationView> {
    const url = new URL(`${this.baseUrl}/api/v1/reporting/conversation`);
    url.searchParams.set('periodStart', periodStart);
    const response = await fetch(url, { headers: this.authHeaders() });
    if (!response.ok) {
      throw new Error(`employment-insurance-bff returned ${response.status} for /api/v1/reporting/conversation`);
    }
    return (await response.json()) as GuidedConversationView;
  }

  async sendGuidedTurn(periodStart: string, event: GuidedEvent): Promise<GuidedConversationView> {
    const response = await fetch(`${this.baseUrl}/api/v1/reporting/turn`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ periodStart, event }),
    });
    if (!response.ok) {
      throw new Error(`employment-insurance-bff returned ${response.status} for POST /api/v1/reporting/turn`);
    }
    return (await response.json()) as GuidedConversationView;
  }

  async submitGuidedReport(periodStart: string): Promise<EiReport> {
    const response = await fetch(`${this.baseUrl}/api/v1/reporting/submit`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ periodStart }),
    });
    if (!response.ok) {
      throw new Error(`employment-insurance-bff returned ${response.status} for POST /api/v1/reporting/submit`);
    }
    return (await response.json()) as EiReport;
  }
}
